import React from 'react';
import {
  Box, Paper, Container, TextField, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Stack, Divider, Tooltip, TableContainer,
  CircularProgress, Snackbar, Alert, Chip, RadioGroup, FormControlLabel, Radio, LinearProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Refresh, RestartAlt } from '@mui/icons-material';

import Client from '../Client';
import Config from '../ClientConfig';

// ---------- helpers ----------
const ymKey = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// number formatting helpers
const fmtFixed = (n, d) => {
  const v = Number(n);
  if (!isFinite(v)) return '-';
  return v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmt0 = (n) => fmtFixed(Math.round(Number(n) || 0), 0);
const fmt2 = (n) => fmtFixed(n, 2);
const fmt3 = (n) => fmtFixed(n, 3);

const urlJoin = (...parts) =>
  parts
    .filter(Boolean)
    .map((s, i) => (i === 0 ? String(s).replace(/\/+$/,'') : String(s).replace(/^\/+|\/+$/g,'')))
    .join('/');

// API roots
const API_URL_PROD_BREAKDOWN =
  (Config.API_URL_REPORTS_MONTH_CLOSE || '/api/reports/month-close/') + 'prod-breakdown/';
const API_URL_PRODUCTS = Config.API_URL_PRODUCT + 'list_products/';
const API_URL_MONTH_CLOSE = Config.API_URL_REPORTS_MONTH_CLOSE;

// robust recipe endpoint resolution
const RESOLVE_RECIPE_URL = (clientInstance) => {
  const fromConfig = Config.API_URL_PRODUCT_RECIPE;
  const fromClient = Client.API_URL_PRODUCT_RECIPE;
  const fromClientBase = clientInstance?.BASE_API_URL ? urlJoin(clientInstance.BASE_API_URL, 'productRecipe/') : null;
  return fromConfig || fromClient || fromClientBase || '/api/productRecipe/';
};

class ProdBreakdowns extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),

      // lookups
      locations: [],
      locationOpt: null,
      productNameMap: {},
      productOptions: [],   // finished-only (status=2, !isPurchased)

      // filters
      month: nowYM(),
      selectedProducts: [],

      // data
      rows: [],
      filtered: [],
      selectedKey: '',

      // recipe cache: { [finishedProductId]: [{ product, quantity }] }
      recipeByProduct: {},

      // fallback price cache: key = `${locationIref}|${period}` -> Map(ingredientId -> fallbackPrice)
      fallbackPriceCache: {},

      // paging
      page: 0,
      rowsPerPage: -1,

      // ui
      loading: false,
      errorDialog: { open: false, detail: '' },
      toastOpen: false,
      toastMsg: '',
      toastSeverity: 'success',
      loadingRecipe: false,

      // export dialog
      exportOpen: false,
      exportMode: 'this', // 'this' | 'all'
      exporting: false,
      exportDone: 0,
      exportTotal: 0,
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  // ---------- util ui ----------
  openError = (detail) => this.setState({ errorDialog: { open: true, detail: detail || 'Error' } });
  closeError = () => this.setState({ errorDialog: { open: false, detail: '' } });
  showToast = (msg, severity = 'success') =>
    this.setState({ toastOpen: true, toastMsg: msg, toastSeverity: severity });
  closeToast = () => this.setState({ toastOpen: false, toastMsg: '' });

  // ---------- name helpers ----------
  getLocationName = (locId) => {
    if (!locId) return '';
    const l = (this.state.locations || []).find(ll =>
      String(ll.iref || ll.id || ll.uid) === String(locId)
    );
    return l ? (l.name || l.description || String(locId)) : String(locId);
  };

  getProductName = (id) => {
    if (!id) return '';
    const key = String(id);
    const { productNameMap, productOptions } = this.state;
    return productNameMap[key]
      || (productOptions.find(o => o.value === key)?.label)
      || key;
  };

  // ---------- bootstrap ----------
  bootstrap = async () => {
    try {
      await Promise.all([this.loadProducts(), this.loadLocations()]);
      this.setState(prev => {
        const first = (prev.locations || [])[0];
        const val = first ? String(first.iref || first.id || first.uid || '') : '';
        const label = first ? this.getLocationName(val) : '';
        return { locationOpt: first ? { value: val, label } : null };
      }, () => {
        if (this.state.locationOpt) this.loadList();
      });
    } catch (e) {
      this.openError(String(e?.message || e));
    }
  };

  loadLocations = async () => {
    const resp = this.state.client.getData(Config.API_URL_LOCATIONS + 'list_locations/', { status: 1 }) || {};
    const locations = Array.isArray(resp.locations) ? resp.locations : [];
    locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    this.setState({ locations });
  };

  loadProducts = async () => {
    const prodResp = this.state.client.getData(API_URL_PRODUCTS, { status: 2, orderBy_name: true }) || {};
    const prods = Array.isArray(prodResp.products)
      ? prodResp.products
      : (Array.isArray(prodResp) ? prodResp : (Array.isArray(prodResp.results) ? prodResp.results : []));

    const productNameMap = {};
    prods.forEach(p => {
      const pid = p.iref || p.id || p.uid || p.code;
      const name = p.name || p.productName || p.description || p.code || String(pid);
      if (pid) productNameMap[String(pid)] = String(name);
    });

    // finished-only like ProductRecipe.js (status=2 & not purchased)
    const productOptions = prods
      .filter(p => Number(p.status) === 2 && (p.isPurchased === false || Number(p.isPurchased) === 0))
      .map(p => ({
        value: String(p.iref || p.id || p.uid || p.code),
        label: productNameMap[String(p.iref || p.id || p.uid || p.code)]
      }));

    this.setState({ productNameMap, productOptions });
  };

  buildQueryParams = () => {
    const { locationOpt, month } = this.state;
    if (!locationOpt || !locationOpt.value) return null;
    const [year, m] = String(month || nowYM()).split('-').map(s => parseInt(s, 10));
    return { year, month: m, location: locationOpt.value };
  };

  loadList = () => {
    const params = this.buildQueryParams();
    if (!params) {
      this.showToast('Select a location first', 'warning');
      return;
    }

    this.setState({ loading: true, rows: [], filtered: [], selectedProducts: [], page: 0 }, () => {
      try {
        // preload fallback prices for selected period+location (iref)
        this.ensureFallbackPricesForPeriod();

        const data = this.state.client.getData(API_URL_PROD_BREAKDOWN, params);
        const rows = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);

        rows.sort((a, b) => (a.finishedProductName || '').localeCompare(b.finishedProductName || ''));

        const filtered = this.applyFilters(rows, []);
        let selectedKey = this.state.selectedKey;
        if (!selectedKey && filtered.length) {
          const f = filtered[0];
          selectedKey = `${f.finishedProduct}|${ymKey(f.year, f.month)}`;
        }
        this.setState({ rows, filtered, selectedKey, loading: false }, async () => {
          if (this.state.selectedKey) {
            const row = this.getSelectedRow();
            if (row) {
              await this.loadRecipeFor(row.finishedProduct, { force: false });
            }
          }
        });
      } catch (e) {
        this.setState({ loading: false });
        this.openError(String(e?.message || e));
      }
    });
  };

  applyFilters = (rows, selectedProducts) => {
    let res = rows.slice();
    const sel = (selectedProducts || []).map(o => String(o.value));
    if (sel.length) res = res.filter(r => sel.includes(String(r.finishedProduct)));
    return res;
  };

  onProductsFilterChange = (_e, opts) => {
    const filtered = this.applyFilters(this.state.rows, opts || []);
    this.setState({ selectedProducts: opts || [], filtered, page: 0 });
  };

  clearFilters = () => {
    const filtered = this.applyFilters(this.state.rows, []);
    this.setState({ selectedProducts: [], filtered, page: 0 });
  };

  setSelected = (row) => {
    const key = `${row.finishedProduct}|${ymKey(row.year, row.month)}`;
    this.setState({ selectedKey: key }, async () => {
      await this.loadRecipeFor(row.finishedProduct, { force: false });
    });
  };

  getSelectedRow = () => {
    const { selectedKey, filtered } = this.state;
    if (!selectedKey) return null;
    const [pidStr, ym] = selectedKey.split('|');
    const [y, m] = ym.split('-').map(n => parseInt(n, 10));
    return filtered.find(r => String(r.finishedProduct) === String(pidStr) && r.year === y && r.month === m) || null;
  };

  // ---------- recipe loading ----------
  refreshRecipeFor = async (productId) => {
    await this.loadRecipeFor(productId, { force: true });
  };

  loadRecipeFor = async (productId, { force = false } = {}) => {
    if (!productId) return;
    const key = String(productId);
    if (!force && this.state.recipeByProduct[key]) return; // already cached
    this.setState({ loadingRecipe: true });
    try {
      const recipeUrl = RESOLVE_RECIPE_URL(this.state.client);

      // try filtered fetch first (if server supports ?product=)
      let resp = this.state.client.getData(recipeUrl, { product: productId }) || {};

      // If shape unexpected or empty, try unfiltered list as a fallback
      let list = Array.isArray(resp.productRecipe)
        ? resp.productRecipe
        : (Array.isArray(resp.results) ? resp.results : (Array.isArray(resp) ? resp : []));

      if (!Array.isArray(list) || list.length === 0) {
        resp = this.state.client.getData(recipeUrl) || {};
        list = Array.isArray(resp.productRecipe)
          ? resp.productRecipe
          : (Array.isArray(resp.results) ? resp.results : (Array.isArray(resp) ? resp : []));
      }

      const recipe =
        list.find(x => String(x?.product?.iref || x?.product) === String(productId)) || list[0];

      const items = Array.isArray(recipe?.recipeItem_recipe)
        ? recipe.recipeItem_recipe.map(it => ({
            product: String(it.product),
            quantity: Number(it.quantity || 0),
          }))
        : [];

      this.setState(prev => ({
        recipeByProduct: { ...prev.recipeByProduct, [key]: items },
        loadingRecipe: false
      }));
    } catch (e) {
      this.setState({ loadingRecipe: false });
      this.showToast(`Recipe load failed: ${String(e?.message || e)}`, 'warning');
    }
  };

  // ---------- fallback prices (SELECTED period, by LOCATION IREF, all ingredients) ----------
  // Build + cache: Map(ingredientId -> fallbackPrice) for the selected period and chosen location (by IREF).
  ensureFallbackPricesForPeriod = async () => {
    const locId = this.state.locationOpt?.value; // iref/id expected by backend
    const period = this.state.month || nowYM();
    if (!locId || !period) return;

    const cacheKey = `${String(locId)}|${period}`; // cache by iref + period
    if (this.state.fallbackPriceCache[cacheKey]) return; // already cached

    try {
      const [yearStr, monthStr] = period.split('-');

      // Send LOCATION IREF to Month Close endpoint
      const resp = this.state.client.getData(API_URL_MONTH_CLOSE, {
        period,
        year: yearStr,
        month: monthStr,
        location: String(locId),
      }) || {};

      // New payload shape example provided:
      // { inventoryClose: [{ inventoryCloseLines_invClose: [ { product, price, cogaQty, cogaCost, consumedQty, consumedCost, ... } ] }] }
      const closes = Array.isArray(resp?.inventoryClose)
        ? resp.inventoryClose
        : (Array.isArray(resp?.results) ? resp.results : (Array.isArray(resp) ? resp : [resp]));

      const pickFallback = (line) => {
        // 1) consumed => unit from consumedCost/consumedQty
        const cq = Number(line?.consumedQty ?? 0);
        const cc = Number(line?.consumedCost ?? 0);
        const unitConsumed = cq > 0 && isFinite(cc) ? (cc / cq) : 0;
        if (unitConsumed > 0) return unitConsumed;
        // 2) price (unit field)
        const unitPrice = Number(line?.price ?? 0);
        if (isFinite(unitPrice) && unitPrice > 0) return unitPrice;
        // 3) coga => unit from cogaCost/cogaQty
        const gq = Number(line?.cogaQty ?? 0);
        const gc = Number(line?.cogaCost ?? 0);
        const unitCoga = gq > 0 && isFinite(gc) ? (gc / gq) : 0;
        if (unitCoga > 0) return unitCoga;
        return 0;
      };

      const map = new Map();
      (closes || []).forEach(cl => {
        const lines = Array.isArray(cl?.inventoryCloseLines_invClose) ? cl.inventoryCloseLines_invClose : [];
        lines.forEach(line => {
          const ingId = String(line?.product ?? line?.ingredient ?? '');
          if (!ingId) return;
          if (map.has(ingId)) return; // keep first found non-zero
          const fb = pickFallback(line);
          if (fb > 0) map.set(ingId, fb);
        });
      });

      this.setState(prev => ({
        fallbackPriceCache: { ...prev.fallbackPriceCache, [cacheKey]: map }
      }));
    } catch (e) {
      // quietly ignore; fallback simply won't exist
    }
  };

  // Get effective price for an ingredient: current price_used OR location-period fallback (consumed -> price -> coga)
  getEffectivePrice = (ingredientId, currentPrice) => {
    const priceNow = Number(currentPrice || 0);
    if (priceNow > 0) return priceNow;

    const locId = this.state.locationOpt?.value;
    const period = this.state.month || nowYM();
    if (!locId || !period) return 0;

    const cacheKey = `${String(locId)}|${period}`; // iref-based key
    const map = this.state.fallbackPriceCache[cacheKey];
    if (!map) return 0;

    return Number(map.get(String(ingredientId)) || 0);
  };

  // ---------- comparison builder (cost-first, with fallback for recipe valuation) ----------
  buildComparison = (breakdown, recipeItems /* rowContext not needed anymore */) => {
    const costMap = new Map(); // ingredient -> { qty, price, lineValue }
    (breakdown || []).forEach(b => {
      const ing = String(b.ingredient);
      const qty = Number(b.qty_per_unit || 0);
      const price = Number(b.price_used || 0);
      costMap.set(ing, { qty, price, lineValue: qty * price });
    });

    const recipeMap = new Map(); // ingredient -> qty (per unit)
    (recipeItems || []).forEach(r => {
      recipeMap.set(String(r.product), Number(r.quantity || 0));
    });

    const ingSet = new Set([...costMap.keys(), ...recipeMap.keys()]);
    const lines = [];
    let recipeUnitCost = 0; // Σ(recipeQty * effective price)

    ingSet.forEach(ing => {
      const c = costMap.get(ing) || { qty: 0, price: 0, lineValue: 0 };
      const rQty = recipeMap.get(ing) || 0;

      // Use current price if present; else fallback from Month Close (location iref + selected period)
      const priceDisplay = this.getEffectivePrice(ing, c.price);

      const recipeValue = rQty * priceDisplay; // valuation uses effective price
      recipeUnitCost += recipeValue;

      lines.push({
        ingredient: ing,
        costQty: c.qty,
        priceUsedDisplay: priceDisplay, // shown + used for recipe valuation
        priceUsedRaw: c.price,          // current breakdown price_used
        lineValue: c.lineValue,         // actual month-close line value
        recipeQty: rQty,
        recipeValue,
      });
    });

    return { lines, recipeUnitCost };
  };

  // ---------- CSV: selected breakdown (details) ----------
  exportSelectedCsv = () => {
    const r = this.getSelectedRow();
    if (!r) {
      this.showToast('Nothing selected to export.', 'warning');
      return;
    }

    const recipeItems = this.state.recipeByProduct[String(r.finishedProduct)] || [];
    const { lines /*, recipeUnitCost */ } = this.buildComparison(r.breakdown || [], recipeItems || []);

    const rows = [
      ['summary'],
      ['period', ymKey(r.year, r.month)],
      ['location', this.getLocationName(r.location) || ''],
      ['finished_product', r.finishedProductName || ''],
      [],
      // headers (qty=3, values=2)
      ['ingredient', 'cost_qty_per_unit', 'price_used', 'total_cost', 'recipe_qty_per_unit', 'recipe_value'],
    ];

    lines.forEach(ln => {
      rows.push([
        this.getProductName(ln.ingredient),
        fmt3(ln.costQty),
        fmt2(ln.priceUsedDisplay),
        fmt2(ln.lineValue),
        fmt3(ln.recipeQty),
        fmt2(ln.recipeValue),
      ]);
    });

    const csv = "\uFEFF" + rows.map(row => row.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fname = `prod-breakdown_${(r.finishedProductName || 'product').replace(/\s+/g,'-').toLowerCase()}_${ymKey(r.year, r.month)}.csv`;
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- CSV: list (this location) ----------
  exportCostsCsvThisLocation = () => {
    const { filtered } = this.state;
    if (!filtered.length) {
      this.showToast('No rows to export.', 'warning');
      return;
    }
    const rows = [
      ['summary'],
      ['location', this.getLocationName(this.state.locationOpt?.value)],
      ['month', this.state.month],
      ['rows', String(filtered.length)],
      [],
      ['product', 'period', 'produced_qty', 'unit_cost', 'total_value']
    ];
    filtered.forEach(r => {
      rows.push([
        r.finishedProductName || '',
        ymKey(r.year, r.month),
        fmt0(r.producedQty),
        fmt2(r.computedUnitCost),
        fmt2(r.totalProductionValue),
      ]);
    });

    const csv = rows.map(row => row.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const loc = this.getLocationName(this.state.locationOpt?.value);
    const fname = `prod-costs_${(loc || 'location')}_${this.state.month}.csv`.replace(/\s+/g,'-');
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- CSV: list (all locations for selected month) ----------
  exportCostsCsvAllLocations = async () => {
    const [year, m] = String(this.state.month || nowYM()).split('-').map(s => parseInt(s, 10));
    const ids = (this.state.locations || []).map(l => String(l.iref || l.id || l.uid)).filter(Boolean);
    if (!ids.length) {
      this.showToast('No locations available to export.', 'warning');
      return;
    }

    this.setState({ exporting: true, exportDone: 0, exportTotal: ids.length });

    const detailRows = [];

    for (let i = 0; i < ids.length; i++) {
      try {
        const params = { year, month: m, location: ids[i] };
        const resp = this.state.client.getData(API_URL_PROD_BREAKDOWN, params);
        const list = Array.isArray(resp) ? resp : (Array.isArray(resp?.results) ? resp.results : []);
        list.forEach(r => {
          detailRows.push([
            this.getLocationName(r.location),
            r.finishedProductName || '',
            ymKey(r.year, r.month),
            fmt0(r.producedQty),
            fmt2(r.computedUnitCost),
            fmt2(r.totalProductionValue),
          ]);
        });
      } catch (e) {
        // skip this location, continue
      } finally {
        this.setState({ exportDone: i + 1 });
      }
    }

    const rows = [
      ['summary'],
      ['month', this.state.month],
      ['locations', String(ids.length)],
      ['rows', String(detailRows.length)],
      [],
      ['location', 'product', 'period', 'produced_qty', 'unit_cost', 'total_value'],
      ...detailRows
    ];

    const csv = rows.map(row => row.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fname = `prod-costs_ALL_${this.state.month}.csv`;
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);

    this.setState({ exporting: false, exportOpen: false });
  };

  // ---------- UI ----------
  renderToolbar() {
    const {
      locations,
      locationOpt,
      month,
      loading,
      productOptions,
      selectedProducts,
      loadingRecipe
    } = this.state;

    const locationOptions = (locations || []).map(l => {
      const value = String(l.iref || l.id || l.uid || '');
      return { value, label: this.getLocationName(value) };
    });

    const fieldSx = {
      '& .MuiInputBase-root': {
        height: 34,
        borderRadius: '12px',
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
        bgcolor: '#fff',
      },
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
      },
      '& .MuiSelect-select': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
      },
    };

    const buttonSx = {
      height: 34,
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 750,
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
    };

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: {
            xs: 0.75,
            sm: 1,
          },
          mb: 1,
          borderRadius: '14px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            alignItems: 'center',
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Autocomplete
            size="small"
            options={locationOptions}
            value={locationOpt}
            onChange={(_e, v) =>
              this.setState({ locationOpt: v || null }, () => {
                if (v) this.loadList();
              })
            }
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                required
                sx={fieldSx}
              />
            )}
            sx={{
              width: {
                xs: '100%',
                sm: 230,
                md: 260,
              },
              minWidth: 0,
              flexShrink: 0,
            }}
          />

          <TextField
            label="Month"
            type="month"
            value={month}
            onChange={(e) =>
              this.setState({ month: e.target.value }, () => {
                if (this.state.locationOpt) this.loadList();
              })
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{
              width: {
                xs: 145,
                sm: 150,
              },
              flexShrink: 0,
              ...fieldSx,
            }}
          />

          <Autocomplete
            multiple
            size="small"
            options={productOptions}
            value={selectedProducts}
            onChange={this.onProductsFilterChange}
            getOptionLabel={(o) => o?.label || String(o?.value || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Finished products"
                sx={fieldSx}
              />
            )}
            sx={{
              minWidth: {
                xs: '100%',
                sm: 260,
                md: 320,
              },
              flex: {
                xs: '1 1 100%',
                sm: '1 1 260px',
              },
            }}
          />

          <Tooltip title="Refresh list + recipe + fallback prices">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  this.loadList();
                  this.ensureFallbackPricesForPeriod();
                  const r = this.getSelectedRow();
                  if (r) this.refreshRecipeFor(r.finishedProduct);
                }}
                disabled={loading}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '12px',
                  border: '1px solid rgba(26, 35, 126, 0.12)',
                  color: '#1a237e',
                  bgcolor: 'rgba(26, 35, 126, 0.06)',
                }}
              >
                {loading ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Reset product filters">
            <IconButton
              size="small"
              onClick={this.clearFilters}
              sx={{
                width: 34,
                height: 34,
                borderRadius: '12px',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.06)',
              }}
            >
              <RestartAlt fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />

          <Button
            size="small"
            variant="outlined"
            onClick={() => this.setState({ exportOpen: true })}
            sx={buttonSx}
          >
            Export
          </Button>

          <Tooltip title="Reload recipe only">
            <span>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const r = this.getSelectedRow();
                  if (r) this.refreshRecipeFor(r.finishedProduct);
                }}
                disabled={loadingRecipe}
                sx={{
                  ...buttonSx,
                  color: '#1a237e',
                }}
              >
                {loadingRecipe ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                Recipe
              </Button>
            </span>
          </Tooltip>

          <Chip
            size="small"
            label={`${this.state.filtered.length} breakdowns`}
            variant="outlined"
            sx={{
              height: 28,
              borderRadius: '10px',
              fontWeight: 650,
              color: '#64748b',
            }}
          />
        </Box>
      </Paper>
    );
  }

  renderList() {
    const { filtered, page, rowsPerPage, loading, selectedKey } = this.state;
    const start = page * rowsPerPage;
    const rows = rowsPerPage > 0 ? filtered.slice(start, start + rowsPerPage) : filtered;

    const bodyCellSx = {
      py: { xs: 0.55, sm: 0.75, md: 0.8 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: '#111827',
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
      fontVariantNumeric: 'tabular-nums',
    };

    const headerCellSx = {
      py: { xs: 0.5, sm: 0.7, md: 0.85 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 700,
      lineHeight: 1.15,
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
    };

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 0,
          position: 'relative',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          borderRadius: '14px',
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            '& .MuiTableRow-hover:hover': {
              backgroundColor: 'rgba(26, 35, 126, 0.035)',
            },
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: {
                xs: 620,
                sm: 680,
                md: '100%',
              },
              width: '100%',
              tableLayout: {
                xs: 'fixed',
                md: 'auto',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 6,
                    bgcolor: '#f8fafc',
                    backgroundClip: 'padding-box',
                    width: {
                      xs: 210,
                      sm: 260,
                      md: '42%',
                    },
                    minWidth: {
                      xs: 210,
                      sm: 260,
                    },
                    boxShadow: {
                      xs: '8px 0 10px -10px rgba(15, 23, 42, 0.45)',
                      md: 'none',
                    },
                  }}
                >
                  Product
                </TableCell>
                {[
                  ['Produced', { xs: 95, sm: 105, md: '14%' }],
                  ['Unit Cost', { xs: 95, sm: 110, md: '16%' }],
                  ['Total', { xs: 105, sm: 120, md: '16%' }],
                  ['Period', { xs: 90, sm: 95, md: '12%' }],
                ].map(([label, width]) => (
                  <TableCell key={label} sx={{ ...headerCellSx, width }} align="right">
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map(r => {
                const key = `${r.finishedProduct}|${ymKey(r.year, r.month)}`;
                const isSel = selectedKey === key;

                return (
                  <TableRow
                    key={key}
                    hover
                    selected={!!isSel}
                    onClick={() => this.setSelected(r)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        position: 'sticky',
                        left: 0,
                        zIndex: 5,
                        bgcolor: isSel ? '#eef2ff' : '#fff',
                        backgroundClip: 'padding-box',
                        fontWeight: 650,
                        width: {
                          xs: 210,
                          sm: 260,
                          md: '42%',
                        },
                        minWidth: {
                          xs: 210,
                          sm: 260,
                        },
                        boxShadow: {
                          xs: '8px 0 10px -10px rgba(15, 23, 42, 0.45)',
                          md: 'none',
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: -1,
                          width: '1px',
                          height: '100%',
                          bgcolor: 'rgba(15, 23, 42, 0.08)',
                        },
                      }}
                      title={r.finishedProductName || '(unnamed)'}
                    >
                      {r.finishedProductName || '(unnamed)'}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{fmt0(r.producedQty)}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{fmt2(r.computedUnitCost)}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{fmt2(r.totalProductionValue)}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{ymKey(r.year, r.month)}</TableCell>
                  </TableRow>
                );
              })}

              {!rows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={bodyCellSx}>
                    No results
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, newPage) => this.setState({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            this.setState({ rowsPerPage: Number(e.target.value), page: 0 })
          }
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            minHeight: { xs: 44, sm: 52 },
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              minHeight: { xs: 44, sm: 52 },
              px: { xs: 0.5, sm: 1 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.78rem', sm: '0.86rem' },
              m: 0,
            },
          }}
        />
      </Paper>
    );
  }

  renderSummaryChips(recipeUnitCost, recipeTotalValue, r) {
    const items = [
      { label: 'Qty', value: fmt0(r.producedQty) },
      { label: 'UCost', value: fmt2(r.computedUnitCost) },
      { label: 'Total', value: fmt2(r.totalProductionValue) },
      { label: 'R.UCost', value: fmt2(recipeUnitCost) },
      { label: 'R.Total', value: fmt2(recipeTotalValue) },
    ];
    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {items.map((it, idx) => (
          <Chip
            key={idx}
            size="small"
            variant="outlined"
            label={<span><strong>{it.label}:</strong> {it.value}</span>}
            sx={{ fontSize: 14, height: 24 }}
          />
        ))}
      </Stack>
    );
  }

  renderDetails() {
    const r = this.getSelectedRow();

    if (!r) {
      return (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2,
            height: '100%',
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '14px',
            borderColor: 'rgba(15, 23, 42, 0.08)',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Select a row to view details
          </Typography>
        </Paper>
      );
    }

    const recipeItems = this.state.recipeByProduct[String(r.finishedProduct)] || null;
    const { lines, recipeUnitCost } = this.buildComparison(r.breakdown || [], recipeItems || []);
    const recipeTotalValue = (Number(r.producedQty || 0) || 0) * (recipeUnitCost || 0);

    const sumCost = lines.reduce((acc, ln) => acc + (ln.lineValue || 0), 0);
    const sumRecipe = lines.reduce((acc, ln) => acc + (ln.recipeValue || 0), 0);

    const bodyCellSx = {
      py: { xs: 0.55, sm: 0.75, md: 0.8 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: '#111827',
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
      fontVariantNumeric: 'tabular-nums',
    };

    const headerCellSx = {
      py: { xs: 0.5, sm: 0.7, md: 0.85 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 700,
      lineHeight: 1.15,
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
    };

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          borderRadius: '14px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 0.75, sm: 1 },
            py: { xs: 0.65, sm: 0.85 },
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: {
                    xs: '0.84rem',
                    sm: '0.92rem',
                    md: '1rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={r.finishedProductName || '(unnamed)'}
              >
                {r.finishedProductName || '(unnamed)'}
              </Typography>

              <Typography
                sx={{
                  mt: 0.15,
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.72rem',
                    sm: '0.78rem',
                  },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {this.getLocationName(r.location)} · {ymKey(r.year, r.month)}
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              size="small"
              variant="outlined"
              onClick={this.exportSelectedCsv}
              sx={{
                height: 32,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 750,
                flexShrink: 0,
              }}
            >
              Export
            </Button>
          </Stack>

          <Box sx={{ mt: 0.75 }}>
            {this.renderSummaryChips(recipeUnitCost, recipeTotalValue, r)}
          </Box>

          {!recipeItems && (
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                mt: 0.75,
                py: 0.25,
                borderRadius: '12px',
                fontSize: '0.78rem',
              }}
            >
              Recipe not loaded yet or not defined for this product. Use “Recipe” in the toolbar.
            </Alert>
          )}
        </Box>

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: {
                xs: 720,
                sm: 820,
                md: '100%',
              },
              width: '100%',
              tableLayout: {
                xs: 'fixed',
                md: 'auto',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 4,
                    width: {
                      xs: 220,
                      sm: 280,
                      md: '34%',
                    },
                  }}
                >
                  Ingredient
                </TableCell>

                {[
                  ['Cost Qty', { xs: 95, sm: 105, md: '13%' }],
                  ['Price', { xs: 90, sm: 100, md: '12%' }],
                  ['Total Cost', { xs: 105, sm: 120, md: '15%' }],
                  ['Rcp Qty', { xs: 95, sm: 105, md: '13%' }],
                  ['Rcp Val', { xs: 95, sm: 110, md: '13%' }],
                ].map(([label, width]) => (
                  <TableCell key={label} sx={{ ...headerCellSx, width }} align="right">
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {lines.map((ln, idx) => (
                <TableRow key={idx} hover>
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: '#fff',
                      fontWeight: 600,
                      width: {
                        xs: 220,
                        sm: 280,
                        md: '34%',
                      },
                    }}
                    title={this.getProductName(ln.ingredient)}
                  >
                    {this.getProductName(ln.ingredient)}
                  </TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt3(ln.costQty)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt2(ln.priceUsedDisplay)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt2(ln.lineValue)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt3(ln.recipeQty)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt2(ln.recipeValue)}</TableCell>
                </TableRow>
              ))}

              {!lines.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Alert severity="info" variant="outlined">
                      No ingredients to show.
                    </Alert>
                  </TableCell>
                </TableRow>
              )}

              {lines.length > 0 && (
                <TableRow
                  sx={{
                    bgcolor: '#fff',
                    '& .MuiTableCell-root': {
                      borderTop: '1px solid rgba(15, 23, 42, 0.12)',
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      bgcolor: '#fff',
                      fontWeight: 800,
                      width: {
                        xs: 220,
                        sm: 280,
                        md: '34%',
                      },
                      minWidth: {
                        xs: 220,
                        sm: 280,
                      },
                      boxShadow: {
                        xs: '6px 0 8px -8px rgba(15, 23, 42, 0.35)',
                        md: 'none',
                      },
                    }}
                  >
                    Total
                  </TableCell>

                  <TableCell sx={bodyCellSx} />
                  <TableCell sx={bodyCellSx} />

                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                    }}
                    align="right"
                  >
                    {fmt2(sumCost)}
                  </TableCell>

                  <TableCell sx={bodyCellSx} />

                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                    }}
                    align="right"
                  >
                    {fmt2(sumRecipe)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderExportDialog() {
    const { exportOpen, exportMode, exporting, exportDone, exportTotal, filtered } = this.state;
    const canExportThis = filtered.length > 0;

    return (
      <Dialog open={exportOpen} onClose={() => exporting ? null : this.setState({ exportOpen: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Export costs</DialogTitle>
        <DialogContent dividers>
          <RadioGroup
            value={exportMode}
            onChange={(e) => this.setState({ exportMode: e.target.value })}
          >
            <FormControlLabel value="this" control={<Radio />} label="This location (selected month)" />
            <FormControlLabel value="all" control={<Radio />} label="All locations (selected month)" />
          </RadioGroup>

          {exportMode === 'this' && !canExportThis && (
            <Alert severity="info" sx={{ mt: 1 }}>Nothing to export for this location.</Alert>
          )}

          {exporting && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {exportMode === 'all'
                  ? `Exporting ${exportDone}/${exportTotal} locations…`
                  : 'Preparing export…'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ exportOpen: false })} disabled={exporting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (exportMode === 'this') {
                if (!canExportThis) return;
                this.setState({ exporting: true });
                try {
                  await Promise.resolve(this.exportCostsCsvThisLocation());
                } finally {
                  this.setState({ exporting: false, exportOpen: false });
                }
              } else {
                await this.exportCostsCsvAllLocations();
              }
            }}
            disabled={exporting || (exportMode === 'this' && !canExportThis)}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: {
            xs: 720,
            sm: 760,
            md: 680,
            lg: 720,
          },
          maxHeight: {
            xs: 720,
            sm: 760,
            md: 680,
            lg: 720,
          },
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          p: {
            xs: 0.75,
            sm: 1,
            md: 1.25,
          },
          boxSizing: 'border-box',
        }}
      >
        {this.renderToolbar()}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 0.92fr) minmax(0, 1.08fr)',
            },
            gap: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
            {this.renderList()}
          </Box>

          <Box sx={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
            {this.renderDetails()}
          </Box>
        </Box>

        {this.renderExportDialog()}

        <Dialog
          open={this.state.errorDialog.open}
          onClose={this.closeError}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Error</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {this.state.errorDialog.detail}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeError}>OK</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={this.state.toastOpen}
          autoHideDuration={2400}
          onClose={this.closeToast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={this.closeToast}
            severity={this.state.toastSeverity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {this.state.toastMsg}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default ProdBreakdowns;